import React, { createContext, useContext, useReducer, ReactNode, useEffect, useState } from 'react';
import { getUserPreferences, saveUserPreferences } from '../services/preferences';

// Define all setting interfaces
export interface SpatialSettings {
  gridResolution: number;        // ~330ft grid cells (0.001 = ~330ft, 0.0005 = ~160ft)
  dwellRadius: number;          // Dwell detection radius in meters
  stayPointThreshold: number;   // Movement detection threshold in meters
}

export interface TemporalSettings {
  stayTimeThreshold: number;    // Minimum dwell duration in seconds
  daytimeStart: number;         // Day period start hour (0-23)
  daytimeEnd: number;          // Day period end hour (0-23) 
  maxTimeGap: number;          // Max GPS signal gap in seconds
}

export interface RouteSettings {
  minRouteLength: number;       // Minimum GPS points per route
  similarityThreshold: number;  // Route matching percentage (0-100)
  maxSpeed: number;            // mph for realistic filtering
  minSegmentDistance: number;   // Minimum meaningful distance in meters
}

export interface VisualizationSettings {
  heatmapRadius: number;        // Visual circle size in pixels
  heatmapBlur: number;         // Gradient smoothness in pixels
  minIntensity: number;        // Minimum visible color (0.001-1.0)
  colorScheme: 'blue-red' | 'thermal' | 'viridis' | 'custom';
  showDwellMarkers: boolean;   // Show numbered dwell location markers
  showRouteLines: boolean;     // Show route polylines
  showHeatmap: boolean;        // Show heatmap overlay
  enableClustering: boolean;   // Enable data point clustering
  mapStyle: 'streets' | 'satellite' | 'satellite-streets' | 'dark' | 'light' | 'outdoors'; // Map style preference
  trackPointSize: number;       // Size of track points on the map in pixels (default 4)
  // Link Analysis settings
  linkAnalysis: {
    nodeSize: number;           // Base node size in pixels (default 8)
    linkDistance: number;       // Force simulation link distance (default 100)
    chargeStrength: number;     // Force simulation charge strength (default -300)
    linkCurvature: number;      // Link curve amount 0-1 (default 0.3)
    linkOpacity: number;        // Link line opacity 0-1 (default 0.8)
  };
}

export interface QualitySettings {
  gpsAccuracyFilter: boolean;   // Remove low-accuracy GPS points
  maxDistanceJump: 'speed-based' | 'fixed'; // Distance gap handling
  outlierDetection: boolean;    // Remove GPS errors
  fixedDistanceLimit: number;   // Fixed distance limit in meters (when not speed-based)
}

export interface PerformanceSettings {
  maxDwellLocations: number;    // Processing limit for dwell locations
  maxRoutes: number;           // Display limit for routes
  enableCaching: boolean;      // Speed up repeated analyses
  enableParallelProcessing: boolean; // Use multiple CPU cores
}

export interface AppearanceSettings {
  themeMode: 'light' | 'dark' | 'system';  // Theme mode: light, dark, or follow system
  colorScheme: string;  // Color scheme ID (e.g., 'default', 'forest', 'sunset', 'custom_123')
}

export interface CustomColorScheme {
  id: number;
  scheme_name: string;
  description?: string;
  primary_color: string;
  secondary_color: string;
  accent_color?: string;
}

// Complete settings state
export interface AppSettings {
  spatial: SpatialSettings;
  temporal: TemporalSettings;
  routes: RouteSettings;
  visualization: VisualizationSettings;
  quality: QualitySettings;
  performance: PerformanceSettings;
  appearance: AppearanceSettings;
}

// Default settings based on current implementation
export const defaultSettings: AppSettings = {
  spatial: {
    gridResolution: 0.001,      // ~100m grid cells
    dwellRadius: 50.0,          // 50m dwell detection
    stayPointThreshold: 50.0,   // 50m movement threshold
  },
  temporal: {
    stayTimeThreshold: 300,     // 5 minutes minimum dwell
    daytimeStart: 6,           // 6 AM
    daytimeEnd: 18,            // 6 PM
    maxTimeGap: 1200,          // 20 minutes GPS gap
  },
  routes: {
    minRouteLength: 5,          // 5 GPS points minimum
    similarityThreshold: 50.0,  // 50% route similarity
    maxSpeed: 90,              // 90 mph max realistic speed
    minSegmentDistance: 100.0,  // 100m minimum segment
  },
  visualization: {
    heatmapRadius: 20,          // 20px visual radius
    heatmapBlur: 10,           // 10px blur effect
    minIntensity: 0.02,        // 2% minimum intensity (recently changed from 10%)
    colorScheme: 'blue-red',   // Default color scheme
    showDwellMarkers: true,    // Show dwell markers
    showRouteLines: true,      // Show route lines
    showHeatmap: false,        // Heatmap disabled by default
    enableClustering: true,    // Enable clustering by default
    mapStyle: 'dark',          // Default map style
    trackPointSize: 4,         // 4px track point size
    // Link Analysis defaults
    linkAnalysis: {
      nodeSize: 8,              // 8px base node size
      linkDistance: 100,        // 100px link distance
      chargeStrength: -300,     // -300 charge strength
      linkCurvature: 0.3,       // 0.3 curvature
      linkOpacity: 0.8,         // 80% opacity
    },
  },
  quality: {
    gpsAccuracyFilter: true,    // Enable GPS accuracy filtering
    maxDistanceJump: 'speed-based', // Use speed-based distance filtering
    outlierDetection: true,     // Enable outlier detection
    fixedDistanceLimit: 2000,   // 1.2mi fixed distance limit (when not speed-based)
  },
  performance: {
    maxDwellLocations: 100,     // Process max 100 dwell locations
    maxRoutes: 100,            // Display max 100 routes
    enableCaching: true,       // Enable caching for performance
    enableParallelProcessing: false, // Single-threaded by default
  },
  appearance: {
    themeMode: 'system',       // Follow system theme by default
    colorScheme: 'default',    // Ocean Blue color scheme by default
  },
};

// Setting presets for different use cases
export const settingPresets = {
  performance: {
    ...defaultSettings,
    spatial: { ...defaultSettings.spatial, gridResolution: 0.002 }, // Larger grid for speed
    performance: { ...defaultSettings.performance, maxDwellLocations: 50, maxRoutes: 50 },
  },
  accuracy: {
    ...defaultSettings,
    spatial: { ...defaultSettings.spatial, gridResolution: 0.0005 }, // Smaller grid for precision
    temporal: { ...defaultSettings.temporal, stayTimeThreshold: 180 }, // 3min minimum dwell
    visualization: { ...defaultSettings.visualization, minIntensity: 0.01 }, // More sensitive
  },
  visualization: {
    ...defaultSettings,
    visualization: {
      ...defaultSettings.visualization,
      heatmapRadius: 25,        // Larger visual radius
      heatmapBlur: 15,         // More blur for smooth gradients
      minIntensity: 0.05,      // Moderate intensity
    },
  },
};

// Actions for settings reducer
type SettingsAction =
  | { type: 'UPDATE_SPATIAL'; payload: Partial<SpatialSettings> }
  | { type: 'UPDATE_TEMPORAL'; payload: Partial<TemporalSettings> }
  | { type: 'UPDATE_ROUTES'; payload: Partial<RouteSettings> }
  | { type: 'UPDATE_VISUALIZATION'; payload: Partial<VisualizationSettings> }
  | { type: 'UPDATE_QUALITY'; payload: Partial<QualitySettings> }
  | { type: 'UPDATE_PERFORMANCE'; payload: Partial<PerformanceSettings> }
  | { type: 'UPDATE_APPEARANCE'; payload: Partial<AppearanceSettings> }
  | { type: 'LOAD_PRESET'; payload: keyof typeof settingPresets }
  | { type: 'RESET_DEFAULTS' }
  | { type: 'LOAD_FROM_SERVER'; payload: AppSettings };

// Settings reducer
function settingsReducer(state: AppSettings, action: SettingsAction): AppSettings {
  switch (action.type) {
    case 'UPDATE_SPATIAL':
      return { ...state, spatial: { ...state.spatial, ...action.payload } };
    case 'UPDATE_TEMPORAL':
      return { ...state, temporal: { ...state.temporal, ...action.payload } };
    case 'UPDATE_ROUTES':
      return { ...state, routes: { ...state.routes, ...action.payload } };
    case 'UPDATE_VISUALIZATION':
      return { ...state, visualization: { ...state.visualization, ...action.payload } };
    case 'UPDATE_QUALITY':
      return { ...state, quality: { ...state.quality, ...action.payload } };
    case 'UPDATE_PERFORMANCE':
      return { ...state, performance: { ...state.performance, ...action.payload } };
    case 'UPDATE_APPEARANCE':
      return { ...state, appearance: { ...state.appearance, ...action.payload } };
    case 'LOAD_PRESET':
      return settingPresets[action.payload];
    case 'RESET_DEFAULTS':
      return defaultSettings;
    case 'LOAD_FROM_SERVER':
      return action.payload;
    default:
      return state;
  }
}

// Context interface
interface SettingsContextType {
  settings: AppSettings;
  updateSpatial: (updates: Partial<SpatialSettings>) => void;
  updateTemporal: (updates: Partial<TemporalSettings>) => void;
  updateRoutes: (updates: Partial<RouteSettings>) => void;
  updateVisualization: (updates: Partial<VisualizationSettings>) => void;
  updateQuality: (updates: Partial<QualitySettings>) => void;
  updatePerformance: (updates: Partial<PerformanceSettings>) => void;
  updateAppearance: (updates: Partial<AppearanceSettings>) => void;
  loadPreset: (preset: keyof typeof settingPresets) => void;
  resetDefaults: () => void;
}

// Create context
const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

// Settings provider component
interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Monitor authentication state from localStorage (token-based)
  // Note: AuthContext doesn't store 'user' in localStorage, only 'access_token'
  useEffect(() => {
    let wasAuthenticated = isAuthenticated;
    
    const checkAuth = () => {
      const token = localStorage.getItem('access_token');
      const hasValidToken = !!token && token !== 'null' && token !== 'undefined';
      
      // Only update state and log if auth status actually changed
      if (hasValidToken !== wasAuthenticated) {
        setIsAuthenticated(hasValidToken);
        wasAuthenticated = hasValidToken;
        if (hasValidToken) {
          console.log('[Settings] Auth detected - token present');
        } else {
          console.log('[Settings] Auth cleared - logged out');
        }
      }
    };

    checkAuth();
    
    // Check auth state periodically (for login/logout detection)
    const interval = setInterval(checkAuth, 2000);
    return () => clearInterval(interval);
  }, []);
  
  // Load initial settings from localStorage if available
  const loadInitialSettings = (): AppSettings => {
    try {
      const saved = localStorage.getItem('appSettings');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Deep merge with defaults to ensure all nested settings are included
        const merged: AppSettings = {
          spatial: { ...defaultSettings.spatial, ...parsed.spatial },
          temporal: { ...defaultSettings.temporal, ...parsed.temporal },
          routes: { ...defaultSettings.routes, ...parsed.routes },
          visualization: { 
            ...defaultSettings.visualization, 
            ...parsed.visualization,
            // Deep merge linkAnalysis settings
            linkAnalysis: {
              ...defaultSettings.visualization.linkAnalysis,
              ...parsed.visualization?.linkAnalysis,
            },
          },
          quality: { ...defaultSettings.quality, ...parsed.quality },
          performance: { ...defaultSettings.performance, ...parsed.performance },
          appearance: { ...defaultSettings.appearance, ...parsed.appearance },
        };
        console.log('[Settings] Loaded from localStorage:', merged.appearance);
        return merged;
      }
    } catch (error) {
      console.error('[Settings] Error loading from localStorage:', error);
    }
    console.log('[Settings] Using defaults');
    return defaultSettings;
  };

  const [settings, dispatch] = useReducer(settingsReducer, loadInitialSettings());

  // Track if we need to do initial server save (for 404 case)
  const [needsInitialSave, setNeedsInitialSave] = useState(false);

  // Load settings from server when authenticated
  useEffect(() => {
    const loadServerSettings = async () => {
      if (!isAuthenticated) {
        console.log('[Settings] Not authenticated, skipping server load');
        setIsLoading(false);
        return;
      }

      console.log('[Settings] Loading from server (authenticated)...');

      try {
        const serverPrefs = await getUserPreferences();
        console.log('[Settings] Loaded from server:', serverPrefs.appearance);
        
        // Convert server format to app format with deep merge
        const serverSettings: AppSettings = {
          appearance: {
            themeMode: serverPrefs.appearance?.themeMode || defaultSettings.appearance.themeMode,
            colorScheme: serverPrefs.appearance?.colorScheme || defaultSettings.appearance.colorScheme,
          },
          spatial: { ...defaultSettings.spatial, ...serverPrefs.spatial },
          temporal: { ...defaultSettings.temporal, ...serverPrefs.temporal },
          routes: { ...defaultSettings.routes, ...serverPrefs.routes },
          visualization: { 
            ...defaultSettings.visualization, 
            ...serverPrefs.visualization,
            // Deep merge linkAnalysis settings
            linkAnalysis: {
              ...defaultSettings.visualization.linkAnalysis,
              ...serverPrefs.visualization?.linkAnalysis,
            },
          },
          quality: { ...defaultSettings.quality, ...serverPrefs.quality },
          performance: { ...defaultSettings.performance, ...serverPrefs.performance },
        };

        dispatch({ type: 'LOAD_FROM_SERVER', payload: serverSettings });
        
        // Also update localStorage with server data
        localStorage.setItem('appSettings', JSON.stringify(serverSettings));
        console.log('[Settings] Updated localStorage with server settings');
      } catch (error: any) {
        console.warn('[Settings] Server load error:', {
          status: error?.response?.status,
          statusText: error?.response?.statusText,
          data: error?.response?.data,
          message: error?.message
        });
        // If 404, user has no server preferences yet - flag for initial save
        if (error?.response?.status === 404) {
          console.log('[Settings] No server preferences found (404), will save current settings...');
          setNeedsInitialSave(true);
        } else {
          console.error('[Settings] ❌ Unexpected error loading preferences - check API');
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadServerSettings();
  }, [isAuthenticated]);

  // Handle initial save after 404 (uses current settings state, not stale closure)
  useEffect(() => {
    if (needsInitialSave && isAuthenticated && !isLoading) {
      const doInitialSave = async () => {
        try {
          console.log('[Settings] Saving initial preferences to server:', {
            appearance: settings.appearance,
            token: localStorage.getItem('access_token')?.substring(0, 20) + '...'
          });
          const result = await saveUserPreferences(settings);
          console.log('[Settings] ✅ Initial preferences saved successfully:', result);
          setNeedsInitialSave(false);
        } catch (error: any) {
          console.error('[Settings] ❌ Failed to create initial preferences:', {
            status: error?.response?.status,
            data: error?.response?.data,
            message: error?.message
          });
          setNeedsInitialSave(false);
        }
      };
      doInitialSave();
    }
  }, [needsInitialSave, isAuthenticated, isLoading, settings]);

  // Save settings to both localStorage and server whenever they change
  useEffect(() => {
    if (isLoading) return; // Don't save during initial load
    if (needsInitialSave) return; // Don't save if we're about to do initial save

    // Save to localStorage
    try {
      localStorage.setItem('appSettings', JSON.stringify(settings));
      console.log('[Settings] Saved to localStorage:', settings.appearance);
    } catch (error) {
      console.error('[Settings] Failed to save to localStorage:', error);
    }

    // Save to server if authenticated
    if (isAuthenticated) {
      const saveToServer = async () => {
        try {
          console.log('[Settings] Attempting to save to server...', {
            appearance: settings.appearance,
            isAuthenticated,
            token: localStorage.getItem('access_token')?.substring(0, 20) + '...'
          });
          const result = await saveUserPreferences(settings);
          console.log('[Settings] ✅ Saved to server successfully:', result);
        } catch (error: any) {
          console.error('[Settings] ❌ Failed to save to server:', {
            status: error?.response?.status,
            statusText: error?.response?.statusText,
            data: error?.response?.data,
            message: error?.message,
            fullError: error
          });
        }
      };

      // Debounce server saves (wait 1 second after last change)
      const timeoutId = setTimeout(saveToServer, 1000);
      return () => clearTimeout(timeoutId);
    } else {
      console.log('[Settings] Not authenticated, skipping server save');
    }
  }, [settings, isAuthenticated, isLoading, needsInitialSave]);

  const contextValue: SettingsContextType = {
    settings,
    updateSpatial: (updates) => dispatch({ type: 'UPDATE_SPATIAL', payload: updates }),
    updateTemporal: (updates) => dispatch({ type: 'UPDATE_TEMPORAL', payload: updates }),
    updateRoutes: (updates) => dispatch({ type: 'UPDATE_ROUTES', payload: updates }),
    updateVisualization: (updates) => dispatch({ type: 'UPDATE_VISUALIZATION', payload: updates }),
    updateQuality: (updates) => dispatch({ type: 'UPDATE_QUALITY', payload: updates }),
    updatePerformance: (updates) => dispatch({ type: 'UPDATE_PERFORMANCE', payload: updates }),
    updateAppearance: (updates) => dispatch({ type: 'UPDATE_APPEARANCE', payload: updates }),
    loadPreset: (preset) => dispatch({ type: 'LOAD_PRESET', payload: preset }),
    resetDefaults: () => dispatch({ type: 'RESET_DEFAULTS' }),
  };

  return (
    <SettingsContext.Provider value={contextValue}>
      {children}
    </SettingsContext.Provider>
  );
};

// Hook to use settings context
export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
